import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Briefcase } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DESTINATIONS } from "@/lib/data";
import { waLink } from "@/lib/site";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = [
  "Flexible", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const fieldCls =
  "bg-[var(--tx-bg)] border-[var(--tx-border)] text-[var(--tx-text)] placeholder:text-[var(--tx-text-2)]/60 focus-visible:ring-[var(--tx-gold)] rounded-none h-12";

export default function EnquiryForm({ defaultDestination = "", source = "" }) {
  const [form, setForm] = useState({
    name: "", agency: "", email: "", phone: "",
    destination: defaultDestination, travel_month: "Flexible", travellers: "2", message: "",
    company_website: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please add your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        destination: form.destination || defaultDestination || "Not specified",
        source: source || "Website form",
      };
      const res = await axios.post(`${API}/enquiries`, payload);
      trackEvent("generate_lead", { source: source || "site", destination: payload.destination, ref: res.data.ref });
      toast.success(`B2B enquiry received — ref ${res.data.ref}. Our trade desk will reach out shortly.`);
      setForm({ name: "", agency: "", email: "", phone: "", destination: defaultDestination, travel_month: "Flexible", travellers: "2", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="enquiry-form" className="space-y-5">
      <div className="inline-flex items-center gap-2 rounded-full border border-[var(--tx-gold)]/40 bg-[var(--tx-gold)]/10 px-3 py-1 text-xs uppercase tracking-[0.18em] text-[var(--tx-gold)]">
        <Briefcase className="h-3.5 w-3.5" /> B2B · Travel-trade enquiry
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">Agency / Company Name</label>
        <Input data-testid="enquiry-agency" className={`mt-2 ${fieldCls}`} value={form.agency} onChange={set("agency")} placeholder="Your travel agency / DMC name" />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">Contact Name</label>
          <Input data-testid="enquiry-name" className={`mt-2 ${fieldCls}`} value={form.name} onChange={set("name")} placeholder="Your name" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">Phone / WhatsApp</label>
          <Input data-testid="enquiry-phone" className={`mt-2 ${fieldCls}`} value={form.phone} onChange={set("phone")} placeholder="+91 …" />
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">Email</label>
        <Input data-testid="enquiry-email" type="email" className={`mt-2 ${fieldCls}`} value={form.email} onChange={set("email")} placeholder="agent@youragency.com" />
      </div>

      {/* Honeypot — invisible to humans, bots fill it. Never blocks genuine agents. */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, overflow: "hidden" }}>
        <label htmlFor="enquiry-company-website">Company website (leave blank)</label>
        <input
          id="enquiry-company-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.company_website}
          onChange={set("company_website")}
        />
      </div>

      <div className="grid gap-5 sm:grid-cols-3">
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">Destination</label>
          <Select value={form.destination} onValueChange={(v) => setForm((f) => ({ ...f, destination: v }))}>
            <SelectTrigger data-testid="enquiry-destination" className={`mt-2 ${fieldCls}`}>
              <SelectValue placeholder="Choose" />
            </SelectTrigger>
            <SelectContent>
              {DESTINATIONS.map((d) => (
                <SelectItem key={d.slug} value={d.name}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">Travel Month</label>
          <Select value={form.travel_month} onValueChange={(v) => setForm((f) => ({ ...f, travel_month: v }))}>
            <SelectTrigger data-testid="enquiry-month" className={`mt-2 ${fieldCls}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">Travellers</label>
          <Input data-testid="enquiry-travellers" className={`mt-2 ${fieldCls}`} value={form.travellers} onChange={set("travellers")} placeholder="2" />
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">Client brief / requirements</label>
        <Textarea data-testid="enquiry-message" className={`mt-2 ${fieldCls} h-28`} value={form.message} onChange={set("message")} placeholder="Client interests, dates, group size, pace, hotel category…" />
      </div>

      <div className="flex flex-col sm:flex-row flex-wrap gap-4 pt-2">
        <button
          type="submit"
          disabled={loading}
          data-testid="enquiry-submit-button"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--tx-gold)] px-8 py-3.5 text-sm font-medium text-[var(--tx-bg)] hover:bg-[var(--tx-text)] transition-colors duration-300 disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {loading ? "Sending…" : "Send B2B Enquiry"}
        </button>
        <a
          href={waLink()}
          target="_blank"
          rel="noreferrer"
          data-testid="enquiry-whatsapp"
          className="inline-flex items-center justify-center gap-2 rounded-full border border-[var(--tx-border)] px-8 py-3.5 text-sm text-[var(--tx-text)] hover:border-[var(--tx-gold)] hover:text-[var(--tx-gold)] transition-colors duration-300"
        >
          Or chat on WhatsApp
        </a>
      </div>
    </form>
  );
}
